import * as React from 'react'
import { useCallback, useMemo } from 'react'

import { XIcon } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import SwipeableWrapper from '~/client/src/shared/components/SwipeableWrapper/SwipeableWrapper'
import Localization from '~/client/src/shared/localization/LocalizationManager'

const CARD_OFFSET_PX = 12
const FULL_CARD_PERCENT = 100

export interface MapCardCarouselProps<T> {
  items: T[]
  selectedId: string | null
  keyExtractor: (item: T) => string
  renderCard: (item: T, isSelected: boolean) => React.ReactNode
  onSelectedChange?: (id: string) => void
  /** Omit on the surfaces where the strip is dismissed by deselecting the pin instead. */
  onClose?: () => void
}

function MapCardCarousel<T>({
  items,
  selectedId,
  keyExtractor,
  renderCard,
  onSelectedChange,
  onClose,
}: MapCardCarouselProps<T>) {
  const selectedIndex = useMemo(() => {
    if (!selectedId) return 0
    const idx = items.findIndex(item => keyExtractor(item) === selectedId)
    return idx >= 0 ? idx : 0
  }, [items, selectedId, keyExtractor])

  const showNext = useCallback(() => {
    if (selectedIndex >= items.length - 1) return
    onSelectedChange?.(keyExtractor(items[selectedIndex + 1]))
  }, [items, selectedIndex, keyExtractor, onSelectedChange])

  const showPrevious = useCallback(() => {
    if (selectedIndex <= 0) return
    onSelectedChange?.(keyExtractor(items[selectedIndex - 1]))
  }, [items, selectedIndex, keyExtractor, onSelectedChange])

  if (!items.length) return null

  const percentTranslate = FULL_CARD_PERCENT * selectedIndex
  const pixelTranslate = selectedIndex * CARD_OFFSET_PX

  return (
    <>
      {onClose && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onClose}
          aria-label={Localization.translator.close}
          className="tw:absolute tw:right-2 tw:top-0 tw:z-10 tw:size-7 tw:rounded-full tw:bg-card tw:shadow-[0px_1px_2px_rgba(0,0,0,0.3)]"
        >
          <XIcon aria-hidden="true" />
        </Button>
      )}
      <SwipeableWrapper
        onSwipedLeft={showNext}
        onSwipedRight={showPrevious}
        trackMouse={true}
        className="tw:overflow-hidden"
      >
        <div
          className="tw:whitespace-nowrap tw:w-[calc(100%-28px)] tw:ml-3 tw:py-2.5 tw:transition-transform tw:duration-500 tw:ease-in-out"
          style={{
            transform: `translateX(calc(-${percentTranslate}% - ${pixelTranslate}px))`,
          }}
        >
          {items.map(item => {
            const id = keyExtractor(item)
            return (
              <div
                key={id}
                className="tw:inline-block tw:align-middle tw:select-none tw:w-full tw:mr-3 tw:rounded-lg tw:bg-card tw:shadow-[0_4px_8px_rgba(0,0,0,0.25)] tw:overflow-hidden tw:[&>div]:border-b-0 tw:[&>div]:outline-none"
              >
                {renderCard(item, id === selectedId)}
              </div>
            )
          })}
        </div>
      </SwipeableWrapper>
    </>
  )
}

export default MapCardCarousel
